import { Check, Pencil, StickyNote, Users } from 'lucide-react'
import type { ScheduleEventDto } from '../../../../lib/api/v2/planning'
import { kindLabel, kindClasses, isConfirmationLive, settledLabel, kindHasFreeLabel, kindHasLightFill, tentativeClasses, tentativeMutedText, hourLabel, dayPositionInSpan, type AlertBadge } from '../planning-helpers'
import { cn } from '../../../../lib/utils'

interface EventBarProps {
  event: ScheduleEventDto
  /** The agenda day this bar is rendered in — enables the "Jx/N" label on Day events. */
  day?: Date
  /** Open Suivi points on THIS work item → corner badge (red = hard, amber = soft) with count. */
  alert?: AlertBadge
  /** Just pointed at from the cockpit — wears a ring until the flash fades (SOLY-PLAN-004). */
  highlighted?: boolean
  /** Click on the bar → open the project detail (MS-22). */
  onClick?: (event: ScheduleEventDto) => void
  /** Click on the hover pencil → open the scheduling fiche (edit). When omitted, the pencil is hidden. */
  onEdit?: (event: ScheduleEventDto) => void
  /**
   * A third line naming who works on the slot (« Karim, Sam +1 »). Set on a « Types » lane only:
   * there the lane says what kind of work it is, not who does it, so the bar has to. The bar's
   * first two lines stay exactly as in the person and crew views.
   */
  assigneesLine?: string
  /** One more tooltip line (the full assignee names behind the abbreviated `assigneesLine`). */
  tooltipExtra?: string
}

/**
 * One event rendered in an agenda cell (agenda round 6): line 1 = client name (bold), line 2 =
 * job-site city · time (Jx/N for multi-day, "8h - 12h" for hours) + a 👥N badge for team jobs.
 * The kind is encoded by the pastel colour + left border (a toolbar legend names them); the postal
 * code lives in the tooltip. Client/city come from the enriched agenda DTO (server-resolved).
 * Click on the bar opens the project detail; a hover pencil (top-right) opens the scheduling fiche
 * (edit) — two intentions, no ambiguity (MS-22). No drag (removed MS-18).
 */
export function EventBar({ event, day, alert, highlighted, onClick, onEdit, assigneesLine, tooltipExtra }: EventBarProps) {
  const isDay = event.durationUnit === 'Days'
  const teamCount = event.assignments.length
  // Jx/N for a multi-day Day event (null for single-day or hour events) — only when we know the cell day.
  // « Jx/N » on every Day event, « J1/1 » on a one-day pose included (Élodie 2026-08-18): one
  // grammar down a column beats a word here and a fraction there. « Journée » survives only where
  // there IS no position to state — the Saturday column of a pose running Friday→Monday, whose
  // span counts working days only.
  const dayPos = isDay && day ? dayPositionInSpan(event.startDate, event.endDate, day) : null
  const dayLabel = dayPos ? `J${dayPos.index}/${dayPos.total}` : 'Journée'
  // Compact "8h-12h" (no spaces): the time must survive narrow cells — the city truncates, not it.
  const label = isDay ? dayLabel : hourLabel(event.startTime, event.durationValue).replace(/ /g, '')
  // RDV: the note is the title (« Rencontrer M. Dupont ») — fallback client, then the kind word.
  const title = (kindHasFreeLabel(event.kind) ? event.label : null) ?? event.clientName ?? kindLabel(event.kind)
  // SOLY-PLAN-003 MS-08 — « 2/3 » next to the name, and ONLY from two créneaux upwards: three bars
  // named « Pose Dupont » on distant days otherwise read as three chantiers. A pose in one block —
  // the nominal case — renders exactly as before, zero noise.
  //
  // It sits on the TITLE line while « J2/3 » stays on the second one, because they answer different
  // questions: which chantier is this (line 1) versus where are we inside this block (line 2). Two
  // fractions on the same line would be unreadable.
  const slotCount = event.slotCount ?? 0
  const slotOrdinal = event.slotOrdinal ?? 0
  const showOrdinal = slotCount > 1 && slotOrdinal > 0
  // SOLY-PLAN-006 MS-05 — the tooltip said « de cette pose », hard-coded, and the marker now rides
  // métré bars too (a métré can be REPEATED). A métré in two passages announcing a pose is the kind
  // of wrong that never raises anything. The bar's own code was already generic: it was the DATA
  // that was missing, and one string.
  const ordinalOwner = event.kind === 'TechnicalSurvey' ? 'de ce métré' : 'de cette pose'
  const place = event.city
  // Confirmation read: solid + ✓ once Confirmé → « ne pas toucher » ; hatched while Proposé →
  // « provisoire, déplaçable » (Elodie field feedback 2026-06-14). SOLY-PLAN-002 opens it to the
  // RDV; internal work has no counterparty, so it carries neither.
  //
  // Once the work item is settled (closed by a report, cancelled, marked non applicable) the bar
  // stops carrying either mark: the work is a fact, and a
  // « à confirmer » hatch on a pose whose report is written is the noise Kitchy's secretary
  // reported (2026-09-01). What does NOT end the question is the date passing — see
  // isConfirmationLive. The kind's colour is untouched by all of this: a closed week still reads
  // pose blue / métré violet / intervention amber, because that is how she sees what she did.
  const confirmable = isConfirmationLive(event)
  const confirmed = confirmable && event.confirmedAt != null
  const tentative = confirmable && event.confirmedAt == null
  // SOLY-REGIE-001 — the tint follows the WORK TYPE: amber for an intervention (a retouche on its
  // own file, or a finition on a file already sold), blue for a placement. One rule where there
  // used to be a special case for finitions — they are simply the interventions we already had.
  //
  // A REPLACEMENT of the kind fill (Tailwind conflicts are not resolved by class order): amber-600,
  // the 500 shade fails AA contrast with white text.
  //
  // `installationNature` is the FALLBACK, not a second rule: during a deployment window the agenda
  // can be served by a backend that does not carry `workType` yet, and a finition losing its tint
  // for ten minutes is a regression a user would report. An unknown workType (stale payload) falls
  // through to the ordinary pose bar rather than to a broken one.
  const isIntervention = event.workType != null
    ? event.workType === 'Intervention'
    : event.installationNature === 'Finition'
  const natureTint = event.kind === 'Installation' && isIntervention
    ? 'bg-amber-600 text-white'
    : null
  // Ink on the secondary line and the team badge, because the fill is light. The question is
  // LUMINOSITY — asking `kindHasFreeLabel` would happen to give the same answer today and the wrong
  // one for a dark-filled kind that carries a free title. A tentative bar is pale whatever its
  // kind, so it answers yes too.
  const isLightFill = tentative || kindHasLightFill(event.kind)
  // On a tentative bar the second line stays in the hue's own 700 shade: a borrowed slate on a blue
  // or violet ground reads as a rendering accident, and loses contrast the tinted palette has.
  // slate-600, not slate-500. The confirmed RDV fill moved up a step, and slate-500 on it is
  // 3.87:1 — the same commit had just moved the tentative line OFF slate-500 for passing AA by
  // one percent, and would otherwise have pushed the confirmed side under it.
  const mutedLine = tentative ? tentativeMutedText(event.kind, isIntervention)
    : (isLightFill ? 'text-slate-600' : 'opacity-85')
  // A note on a pose/métré créneau (SOLY-PLAN-004): it is NOT the bar's title — the chantier stays
  // the identity, three bars named after a note would read as three unrelated jobs. A sticky-note glyph
  // glyph says one exists, the tooltip and the cockpit card say what it holds.
  const slotNote = !kindHasFreeLabel(event.kind) ? event.label : null
  // Derived from the label, not from a second expression that happened to agree with it: the
  // previous form (`workItemStatus != null && !confirmable`) would have reported a settled bar for
  // any future kind that is unconfirmable for reasons other than settlement.
  const settled = settledLabel(event.kind, event.workItemStatus, event.workItemClosedAt)
  const tooltip = [
    kindLabel(event.kind),
    kindHasFreeLabel(event.kind) ? event.label : null,
    event.clientName,
    event.city ? `${event.city}${event.postalCode ? ` ${event.postalCode}` : ''}` : null,
    // « par le client » retiré : la partie qui confirme n'est plus forcément le client, et
    // « à confirmer » posait la question « par qui ? » au moment exact où la réponse change.
    confirmable ? (confirmed ? 'confirmé' : 'proposé') : null,
    // Not « confirmé », not « proposé » — the third answer, and the one the bar used to be unable
    // to give. It replaces the confirmation word rather than joining it: a settled slot has no
    // confirmation state left to state. Which word depends on HOW it settled: « clôturé » is only
    // true of a recorded report, and the drawer already calls the other two annulé / non nécessaire.
    settled,
  ].filter(Boolean).join(' · ') + (slotNote ? `
${slotNote}` : '') + (tooltipExtra ? `
${tooltipExtra}` : '')

  return (
    <div
      className={cn(
        'group/event relative',
        // Day = solid block filling the cell. Hour = "posé dessus" : encart + ombre portée (treatment 1).
        isDay ? 'block w-full' : 'relative z-10 mx-1 block',
      )}
    >
      <button
        type="button"
        onClick={() => onClick?.(event)}
        title={tooltip}
        data-testid={`planning-event-bar-${event.id}`}
        data-kind={event.kind}
        data-nature={event.installationNature ?? 'Initial'}
        data-worktype={event.workType ?? (event.installationNature === 'Finition' ? 'Intervention' : 'Installation')}
        data-unit={event.durationUnit}
        data-confirmed={confirmable ? (confirmed ? 'true' : 'false') : undefined}
        data-settled={settled ? 'true' : undefined}
        className={cn(
          'relative w-full rounded-md px-1.5 py-1 text-left text-xs',
          'min-h-[24px] focus:outline-none focus:ring-2 focus:ring-offset-1',
          'transition group-hover/event:-translate-y-px group-hover/event:brightness-[0.97]',
          // Three mutually exclusive fills, never layered: Tailwind conflicts are not resolved by
          // class order, so exactly one must reach the element.
          tentative ? tentativeClasses(event.kind, isIntervention) : (natureTint ?? kindClasses(event.kind)),
          isDay ? 'block shadow-sm' : 'block shadow-[0_3px_8px_rgba(0,0,0,0.25)]',
          // The focus flash: a ring, never a colour change — the fill already encodes the kind and
          // the nature, and repainting it would say something false for two seconds.
          highlighted && 'ring-2 ring-offset-1 ring-slate-900',
        )}
        data-highlighted={highlighted || undefined}
      >
        <span className="flex items-center gap-1 leading-tight">
          {confirmed && <Check className="h-3 w-3 flex-none" aria-hidden data-testid={`planning-event-confirmed-${event.id}`} />}
          <span className="block truncate font-semibold">{title}</span>
          {showOrdinal && (
            <span
              className={cn('flex-none rounded px-1 text-[10px] font-semibold',
                isLightFill ? 'bg-black/10 text-slate-700' : 'bg-white/25')}
              data-testid={`planning-event-slot-ordinal-${event.id}`}
              title={`Créneau ${slotOrdinal} sur ${slotCount} ${ordinalOwner}`}
            >
              {slotOrdinal}/{slotCount}
            </span>
          )}
        </span>
        <span className={cn('mt-0.5 flex items-center gap-1 text-[10px]', mutedLine)}>
          {place && <span className="min-w-0 truncate">{place} ·</span>}
          <span className="flex-none">{label}</span>
          {slotNote && (
            <StickyNote className="h-2.5 w-2.5 flex-none" aria-hidden
              data-testid={`planning-event-note-${event.id}`} />
          )}
          <span className="min-w-0 flex-1" />
          {/* Team signal: a multi-assignee job reads as one shared job, not N separate ones (finding #7b). */}
          {teamCount > 1 && (
            <span
              className={cn('ml-1 inline-flex flex-none items-center gap-0.5 rounded px-1 font-semibold',
                isLightFill ? 'bg-black/10 text-slate-700' : 'bg-white/25')}
              data-testid={`planning-event-team-${event.id}`}
              title={`Équipe de ${teamCount}`}
            >
              <Users className="h-2.5 w-2.5" />
              {teamCount}
            </span>
          )}
        </span>
        {assigneesLine && (
          <span className={cn('mt-0.5 block truncate text-[10px]', mutedLine)} data-testid={`planning-event-assignees-${event.id}`}>
            {assigneesLine}
          </span>
        )}
      </button>
      {/* Attention badge: this work item has open Suivi points (round-1 « pastille », 2026-06-10). */}
      {alert && alert.count > 0 && (
        <span
          data-testid={`planning-event-alert-${event.id}`}
          title={`${alert.count} point${alert.count > 1 ? 's' : ''} d'attention — voir le Suivi`}
          className={cn(
            'absolute -right-1 -top-1 z-30 flex h-[15px] min-w-[15px] items-center justify-center rounded-full',
            'border-2 border-white px-0.5 text-[9px] font-extrabold text-white shadow-sm',
            alert.hard ? 'bg-red-500' : 'bg-amber-500',
          )}
        >
          {alert.count}
        </span>
      )}
      {onEdit && (
        <button
          type="button"
          title="Modifier la planification"
          aria-label="Modifier la planification"
          data-testid={`planning-event-edit-${event.id}`}
          onClick={(e) => { e.stopPropagation(); onEdit(event) }}
          className="absolute right-0.5 top-0.5 z-20 flex h-5 w-5 items-center justify-center rounded bg-black/40 text-white opacity-0 transition hover:bg-black/60 focus:opacity-100 group-hover/event:opacity-100"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}
